import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { useAuth } from './auth';

/**
 * Seller follows context. Tracks which seller_ids the current buyer follows
 * so that the heart/follow buttons across pages stay in sync without a
 * server round-trip on every render.
 */
const FollowsCtx = createContext(null);

export const FollowsProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user || user.role !== 'buyer') { setIds(new Set()); return; }
    try {
      const { data } = await api.get('/me/follows');
      setIds(new Set((data.items || []).map((i) => i.seller_id)));
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (seller_id) => {
    if (!user) return { ok: false };
    if (ids.has(seller_id)) {
      await api.delete(`/sellers/${seller_id}/follow`);
      setIds((s) => { const n = new Set(s); n.delete(seller_id); return n; });
      return { ok: true, following: false };
    }
    await api.post(`/sellers/${seller_id}/follow`);
    setIds((s) => new Set(s).add(seller_id));
    return { ok: true, following: true };
  };

  return (
    <FollowsCtx.Provider value={{ ids, toggle, refresh, isFollowing: (id) => ids.has(id) }}>
      {children}
    </FollowsCtx.Provider>
  );
};

export const useFollows = () => useContext(FollowsCtx);
